const BlockType = require('../../extension-support/block-type');
const ArgumentType = require('../../extension-support/argument-type');
const Thread = require('../../engine/thread');

const STATUS_DONE = Thread.STATUS_DONE || 4;

class Scratch3Physics {
    constructor (runtime) {
        this.runtime = runtime;

        // Install the tick loop
        if (!runtime._physicsTickInstalled) {
            runtime._physicsTickInstalled = true;
            const originalStep = runtime._step.bind(runtime);
            runtime._step = () => {
                originalStep();
                this.gravityTick();
                this.velocityTick();
            };
        }

        // Stop button cleanup
        runtime.on('PROJECT_STOP_ALL', () => {
            for (const target of runtime.targets) {
                if (target.isStage) continue;
                target._gravityEnabled = false;
                target._velocityY = 0;
                target._velocityX = 0;
                target._gravityThread = null;
            }
        });
    }

    getInfo () {
        return {
            id: 'physics',
            name: 'Physics',
            color1: '#FF6680',
            color2: '#FF3355',
            color3: '#CC3355',

            blocks: [
                {
                    opcode: 'enableGravity',
                    blockType: BlockType.COMMAND,
                    text: 'gravity on [TARGET] pull [AMOUNT] floor [FLOOR] max speed [MAXSPEED]',
                    arguments: {
                        TARGET: {
                            type: ArgumentType.STRING,
                            menu: 'targetMenu',
                            defaultValue: 'THIS'
                        },
                        AMOUNT: {
                            type: ArgumentType.NUMBER,
                            defaultValue: 1
                        },
                        FLOOR: {
                            type: ArgumentType.NUMBER,
                            defaultValue: -180
                        },
                        MAXSPEED: {
                            type: ArgumentType.NUMBER,
                            defaultValue: 15
                        }
                    }
                },
                {
                    opcode: 'removeGravity',
                    blockType: BlockType.COMMAND,
                    text: 'remove gravity from [TARGET]',
                    arguments: {
                        TARGET: {
                            type: ArgumentType.STRING,
                            menu: 'targetMenu',
                            defaultValue: 'THIS'
                        }
                    }
                },
                {
                    opcode: 'jump',
                    blockType: BlockType.COMMAND,
                    text: 'jump [AMOUNT]',
                    arguments: {
                        AMOUNT: {
                            type: ArgumentType.NUMBER,
                            defaultValue: 10
                        }
                    }
                },
                '---',
                {
                    opcode: 'setVelocity',
                    blockType: BlockType.COMMAND,
                    text: 'set velocity vx [VX] vy [VY]',
                    arguments: {
                        VX: {
                            type: ArgumentType.NUMBER,
                            defaultValue: 0
                        },
                        VY: {
                            type: ArgumentType.NUMBER,
                            defaultValue: 0
                        }
                    }
                }
            ],

            menus: {
                targetMenu: {
                    acceptReporters: false,
                    items: ['THIS', 'ALL']
                }
            }
        };
    }

    // ===== TARGET RESOLUTION =====

    resolveTargets (targetArg, util) {
        if (targetArg === 'THIS') {
            return util && util.target ? [util.target] : [];
        }
        if (targetArg === 'ALL') {
            return this.runtime.targets.filter(t => !t.isStage);
        }
        const named = this.runtime.getSpriteTargetByName(targetArg);
        return named ? [named] : [];
    }

    // ===== GRAVITY =====

    enableGravity (args, util) {
        // ── Monitor thread path ──
        if (util.thread._isGravityMonitor) {
            if (!util.target._gravityEnabled ||
                util.target._gravityThread !== util.thread) {
                // Retire cleanly — clear stack so sequencer
                // cannot advance into the user's script.
                util.thread.stack = [];
                util.thread.stackFrame = [];
                util.thread.requestScriptGlowInFrame = false;
                util.thread.status = STATUS_DONE;
                return;
            }
            util.yield();
            return;
        }

        // ── Normal script path ──
        const amount = Number(args.AMOUNT);
        const floor = Number(args.FLOOR);
        const maxSpeed = Number(args.MAXSPEED);
        const sprites = this.resolveTargets(args.TARGET, util);

        for (const sprite of sprites) {
            sprite._gravityEnabled = true;
            sprite._gravityAmount = amount;
            sprite._gravityFloor = floor;
            sprite._maxFallSpeed = isNaN(maxSpeed) ? 15 : maxSpeed;
            if (sprite._velocityY === undefined) sprite._velocityY = 0;

            // Spawn monitor thread — old one will self-retire
            // when it sees _gravityThread !== itself
            const blockId = util.thread.peekStack();
            const monitorThread = this.runtime._pushThread(
                blockId, sprite, {stackClick: false}
            );
            monitorThread._isGravityMonitor = true;
            sprite._gravityThread = monitorThread;
        }
    }

    removeGravity (args, util) {
        const sprites = this.resolveTargets(args.TARGET, util);
        for (const sprite of sprites) {
            sprite._gravityEnabled = false;
            sprite._velocityY = 0;
            sprite._gravityThread = null;
        }
    }

    jump (args, util) {
        const strength = Number(args.AMOUNT);
        const sprite = util.target;
        if (!sprite._gravityEnabled) {
            sprite.setXY(sprite.x, sprite.y + strength);
            return;
        }
        if (sprite.y <= sprite._gravityFloor + 0.5) {
            sprite._velocityY = strength;
        }
    }

    // ===== VELOCITY =====

    setVelocity (args, util) {
        util.target._velocityX = Number(args.VX);
        util.target._velocityY = Number(args.VY);
    }

    velocityTick () {
        if (!this.runtime) return;
        for (const sprite of this.runtime.targets) {
            if (sprite.isStage) continue;
            if (sprite._gravityEnabled) continue;
            const vx = sprite._velocityX || 0;
            const vy = sprite._velocityY || 0;
            if (vx === 0 && vy === 0) continue;
            sprite.setXY(sprite.x + vx, sprite.y + vy);
        }
    }

    // ===== GRAVITY TICK =====

    gravityTick () {
        if (!this.runtime) return;
        for (const sprite of this.runtime.targets) {
            if (sprite.isStage) continue;
            if (!sprite._gravityEnabled) continue;

            const amount = sprite._gravityAmount || 1;
            const floor = sprite._gravityFloor !== undefined ? sprite._gravityFloor : -180;
            const maxFall = sprite._maxFallSpeed || 15;
            const vx = sprite._velocityX || 0;

            if (sprite.y <= floor) {
                sprite.setXY(sprite.x + vx, floor);
                if (sprite._velocityY <= 0) {
                    sprite._velocityY = 0;
                    continue;
                }
            }

            sprite._velocityY = (sprite._velocityY || 0) - amount;

            if (sprite._velocityY < -maxFall) {
                sprite._velocityY = -maxFall;
            }

            const nextY = sprite.y + sprite._velocityY;

            if (nextY <= floor) {
                sprite.setXY(sprite.x + vx, floor);
                sprite._velocityY = 0;
                continue;
            }

            sprite.setXY(sprite.x + vx, nextY);
        }
    }
}

module.exports = Scratch3Physics;
